// =============================================================================
// ui/state/accounts.tsx — loaded accounts.
// -----------------------------------------------------------------------------
// Same discipline as ui/state/preferences.tsx: `accounts` stays NULL until a
// read succeeds, so a failed load renders an error rather than an empty list.
//
// ⚠ "You have no accounts" is a REAL state with its own screen — the empty state
// that invites you to add your first one. That makes a silent failure worse
// here than almost anywhere else: a network blip would show a trader with six
// funded accounts the new-user onboarding, which is not a subtle wrongness.
//
// The load itself already refuses to return accounts without their adjustments
// (io/sync/accounts.ts), so anything that arrives here has its reset history.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { loadAccounts } from '../../io/sync/accounts';
import type { Account } from '../../selectors/accounts';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type AccountsPhase = 'loading' | 'ready' | 'failed';

export interface AccountsState {
  phase: AccountsPhase;
  /** ⚠ NULL until a read succeeded. An empty array means "genuinely none". */
  accounts: readonly Account[] | null;
  error: string | null;
  reload: () => void;
}

const Ctx = createContext<AccountsState | null>(null);

export function useAccounts(): AccountsState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAccounts() outside <AccountsProvider>');
  return v;
}

export function AccountsProvider(
  { sb, userId, children }: { sb: SupabaseClient; userId: string; children: ReactNode },
) {
  const [phase, setPhase] = useState<AccountsPhase>('loading');
  const [accounts, setAccounts] = useState<readonly Account[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      try {
        const { accounts: rows } = await loadAccounts(sb, userId);
        if (cancelled) return;
        setAccounts(rows);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ accounts stays NULL — never []. See the header: an empty array is
        // the new-user screen, and showing that to someone with six funded
        // accounts because a request timed out is not a subtle wrongness.
        setError(reportFailure('accounts', e));
        setPhase('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [sb, userId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const value = useMemo<AccountsState>(
    () => ({ phase, accounts, error, reload }),
    [phase, accounts, error, reload],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
